import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { mediaApiSlice } from '../../../contentTypes/mediaApiSlice';
import { mediaSlidersApiSlice } from '../../../forYou/mediaSlidersApiSlice';
import { resetMediaSliders } from '../../../forYou/mediaSlidersSlice';
import { Button } from '../../../shared/components/button/Button';
import ChevronBackBtn from '../../../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import { AuthContext } from '../../../shared/context/auth-context';
import { useHttpClient } from '../../../shared/hooks/http-hook';
import { GreenCheckOverlay } from '../../../shared/modal/greenCheckOverlay/GreenCheckOverlay';
import { AccountProfile } from '../../../types/models/user';
import De from '../../images/De.svg';
import En from '../../images/En.svg';
import Sv from '../../images/Sv.svg';
import './LanguagesRightMenu.css';
import { LanguageOption } from './LanguageOption';

type LanguagesRightMenuProps = {
  hide: boolean;
  profile: AccountProfile;
  changeMenu: (_menuIndex: number, _action: string) => void;
  saveChanges: (_profile: AccountProfile) => void;
  activeSubMenuId?: number;
};

export function LanguagesRightMenu({
  hide,
  profile,
  changeMenu,
  saveChanges,
  activeSubMenuId = 57,
}: LanguagesRightMenuProps) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const { sendRequest } = useHttpClient();
  const [userProfile, setUserProfile] = useState(profile);
  const [showGreenCheck, setShowGreenCheck] = useState(false);
  const [activeLang, setActiveLang] = useState(profile?.language || 'en');

  const showGreenCheckOverlay = () => {
    setShowGreenCheck(true);
    setTimeout(() => {
      setShowGreenCheck(false);
    }, 2000);
  };

  useEffect(() => {
    setUserProfile((prevState) => ({
      ...prevState,
      language: activeLang,
    }));
  }, [activeLang]);

  const saveProfile = async () => {
    if (userProfile && auth.token) {
      let responseData;
      try {
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/profile`,
          'POST',
          JSON.stringify({
            profile: userProfile,
          })
        );
      } catch (err) {}
      if (responseData) {
        showGreenCheckOverlay();
        auth.languageHandler(activeLang);
        saveChanges(userProfile);
        dispatch(resetMediaSliders());
        dispatch(
          mediaSlidersApiSlice.util.invalidateTags([
            { type: 'MediaSliders', id: 'LIST' },
            { type: 'Feature', id: 'LIST' },
            { type: 'TakeAction', id: 'LIST' },
          ])
        );
        dispatch(
          mediaApiSlice.util.invalidateTags([
            { type: 'SingleMedia', id: 'LIST' },
            { type: 'Masterclass', id: 'LIST' },
            { type: 'Programme', id: 'LIST' },
          ])
        );
      }
    }
  };

  // Handle keyboard navigation for language options
  const handleLanguageKeyDown = (event: React.KeyboardEvent<HTMLFieldSetElement>) => {
    const languageOptions = ['en', 'de', 'sv'];
    const currentIndex = languageOptions.indexOf(activeLang);

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      const nextIndex = currentIndex < languageOptions.length - 1 ? currentIndex + 1 : 0;
      const nextLanguage = languageOptions[nextIndex];
      setActiveLang(nextLanguage);
      // Focus the next radio button
      setTimeout(() => {
        const nextRadio = document.getElementById(`language-radio-${nextLanguage}`);
        if (nextRadio) {
          nextRadio.focus();
        }
      }, 0);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : languageOptions.length - 1;
      const prevLanguage = languageOptions[prevIndex];
      setActiveLang(prevLanguage);
      // Focus the previous radio button
      setTimeout(() => {
        const prevRadio = document.getElementById(`language-radio-${prevLanguage}`);
        if (prevRadio) {
          prevRadio.focus();
        }
      }, 0);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveLang('en');
      setTimeout(() => {
        const firstRadio = document.getElementById('language-radio-en');
        if (firstRadio) {
          firstRadio.focus();
        }
      }, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveLang('sv');
      setTimeout(() => {
        const lastRadio = document.getElementById('language-radio-sv');
        if (lastRadio) {
          lastRadio.focus();
        }
      }, 0);
    }
  };

  return (
    <>
      {showGreenCheck && <GreenCheckOverlay text={t('saved_changes_successfully')} />}
      {userProfile && (
        <div
          role="tabpanel"
          id="panel-languages"
          aria-labelledby="tab-languages"
          tabIndex={activeSubMenuId === 57 ? 0 : -1}
          hidden={activeSubMenuId !== 57}
          className="edit-field lang"
        >
          {hide && (
            <div className="btn-cont ">
              <ChevronBackBtn onClick={() => changeMenu(0, 'back')} />
            </div>
          )}
          <fieldset
            className="language-selection-fieldset"
            aria-labelledby="edit-title"
            onKeyDown={handleLanguageKeyDown}
            role="radiogroup"
          >
            <h2 className="edit-title" id="edit-title">
              {t('PL001')}
            </h2>
            <LanguageOption
              id="en"
              language={{ src: En, name: t('PL002') }}
              active={activeLang === 'en'}
              onChange={() => setActiveLang('en')}
            />
            <LanguageOption
              id="de"
              language={{ src: De, name: t('PL003') }}
              active={activeLang === 'de'}
              onChange={() => setActiveLang('de')}
            />
            <LanguageOption
              id="sv"
              language={{ src: Sv, name: t('PL004') }}
              active={activeLang === 'sv'}
              onChange={() => setActiveLang('sv')}
            />
          </fieldset>
          <Button isFullWidth className="margin-top-24" onClick={saveProfile}>
            {t('S017')}
          </Button>
        </div>
      )}
    </>
  );
}
